/**
 * invitationStore — 청첩장 편집기 핵심 상태 (Zustand)
 *
 * EditorPage에서 사용자가 신랑/신부 정보, 예식장, 갤러리, 설정 등을
 * 수정할 때 변경사항을 추적하고 미리보기에 실시간 반영한다.
 *
 * 주요 기능:
 * - **setInvitation**: API에서 불러온 청첩장 데이터를 스토어에 로드. isDirty를 false로 초기화.
 * - **updateField**: 최상위 필드 타입-안전 업데이트 (예: status, templateId).
 * - **updateNested**: dot-path로 중첩 객체 필드 업데이트 (예: 'groom.name', 'wedding.venue.address').
 * - **isDirty**: 로드 후 변경이 있었는지 추적. 저장 전 페이지 이탈 경고에 사용.
 *
 * @example
 *   const { invitation, updateField, updateNested, isDirty } = useInvitationStore()
 *
 *   // 최상위 필드 변경
 *   updateField('status', 'PUBLISHED')
 *
 *   // 중첩 필드 변경 (data JSONB 기반)
 *   updateNested('data.groom.name', '홍길동')
 *   updateNested('data.wedding.venue.address', '서울시 강남구')
 *
 *   // 저장 전 이탈 경고
 *   if (isDirty && !confirm('저장하지 않은 변경사항이 있습니다. 나가시겠습니까?')) return
 */

import { create } from 'zustand'
import type { Invitation, InvitationData, TemplateConfig } from '@cugumi/shared/schemas'
import { DEFAULT_TEMPLATE_CONFIG } from '@cugumi/shared/schemas'

/** data 필드를 top-level로 펼친 편집용 타입 (컴포넌트가 invitation.groom 등으로 접근) */
export type FlatInvitation = Invitation & InvitationData

interface InvitationState {
  /** 현재 편집 중인 청첩장 데이터. null이면 아직 로드 전 */
  invitation: FlatInvitation | null
  /** 템플릿 설정 */
  templateConfig: TemplateConfig
  /** setInvitation 이후 변경이 있었는지 여부 */
  isDirty: boolean

  /** API 응답 데이터를 스토어에 로드 (isDirty → false) */
  setInvitation: (inv: Invitation, templateConfig?: TemplateConfig) => void
  /** 템플릿 설정 업데이트 */
  setTemplateConfig: (config: TemplateConfig) => void
  /** 최상위 필드를 타입-안전하게 업데이트 */
  updateField: <K extends keyof FlatInvitation>(key: K, value: FlatInvitation[K]) => void
  /** dot-path로 중첩 필드 업데이트 (예: 'groom.name') */
  updateNested: (path: string, value: unknown) => void
  /** isDirty를 false로 리셋 (저장 성공 후 호출) */
  resetDirty: () => void
  /** 스토어를 완전히 초기화 (페이지 이탈 시) */
  clear: () => void
}

/**
 * 객체의 중첩 필드를 dot-path로 불변 업데이트하는 헬퍼.
 * 각 레벨에서 얕은 복사를 수행하여 불변성을 유지한다.
 *
 * @example
 *   setNestedValue({ a: { b: 1 } }, 'a.b', 2)
 *   // → { a: { b: 2 } }
 */
function setNestedValue<T extends Record<string, unknown>>(obj: T, path: string, value: unknown): T {
  const keys = path.split('.')

  // 마지막 키 — 직접 값을 설정
  if (keys.length === 1) {
    return { ...obj, [keys[0]]: value }
  }

  // 재귀적으로 한 레벨씩 내려가며 얕은 복사
  const [head, ...rest] = keys
  const child = (obj[head] ?? {}) as Record<string, unknown>
  return { ...obj, [head]: setNestedValue(child, rest.join('.'), value) }
}

export const useInvitationStore = create<InvitationState>((set) => ({
  invitation: null,
  templateConfig: DEFAULT_TEMPLATE_CONFIG,
  isDirty: false,

  setInvitation: (inv, templateConfig) => {
    const flattened = { ...inv, ...inv.data } as FlatInvitation
    set({
      invitation: flattened,
      isDirty: false,
      ...(templateConfig ? { templateConfig } : {}),
    })
  },

  setTemplateConfig: (config) => set({ templateConfig: config }),

  updateField: (key, value) =>
    set((s) => {
      if (!s.invitation) return s
      return { invitation: { ...s.invitation, [key]: value }, isDirty: true }
    }),

  updateNested: (path, value) =>
    set((s) => {
      if (!s.invitation) return s
      return {
        invitation: setNestedValue(s.invitation as unknown as Record<string, unknown>, path, value) as unknown as FlatInvitation,
        isDirty: true,
      }
    }),

  resetDirty: () => set({ isDirty: false }),
  clear: () => set({ invitation: null, templateConfig: DEFAULT_TEMPLATE_CONFIG, isDirty: false }),
}))
